import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { UploadedImage } from '@/pages/ImageToolsHub';

interface ImageEnhancementToolProps {
  images: UploadedImage[];
}

interface EnhancedImage extends UploadedImage {
  enhancedUrl?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
}

export const ImageEnhancementTool: React.FC<ImageEnhancementToolProps> = ({ images }) => {
  const [enhancedImages, setEnhancedImages] = useState<EnhancedImage[]>(
    images.map(img => ({ ...img, status: 'pending' as const, progress: 0 }))
  );
  const [enhancementType, setEnhancementType] = useState<'dpi' | 'quality' | 'both'>('both');
  const [dpiTarget, setDpiTarget] = useState(300);
  const [qualityLevel, setQualityLevel] = useState<'standard' | 'hd' | 'ultra'>('hd');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Watermark settings
  const [enableWatermark, setEnableWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState('© My Brand');
  const [watermarkPosition, setWatermarkPosition] = useState<'bottom-right'>('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(50);

  const enhanceImages = async () => {
    setIsProcessing(true);

    for (let i = 0; i < enhancedImages.length; i++) {
      const image = enhancedImages[i];
      
      try {
        setEnhancedImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, status: 'processing', progress: 10 } : img
        ));

        // Convert to base64
        const base64Data = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(image.file);
        });

        setEnhancedImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, progress: 30 } : img
        ));

        // Call enhancement function
        const { data, error } = await supabase.functions.invoke('enhance-image-quality', {
          body: {
            imageUrl: base64Data,
            enhancementType,
            dpiTarget: enhancementType === 'dpi' || enhancementType === 'both' ? dpiTarget : undefined,
            qualityLevel: enhancementType === 'quality' || enhancementType === 'both' ? qualityLevel : undefined,
            watermark: enableWatermark ? {
              text: watermarkText,
              position: watermarkPosition,
              opacity: watermarkOpacity,
              size: 'medium',
              color: '#FFFFFF',
              background: true
            } : undefined
          }
        });

        if (error) throw error;

        setEnhancedImages(prev => prev.map(img => 
          img.id === image.id 
            ? { 
                ...img, 
                status: 'completed', 
                progress: 100, 
                enhancedUrl: data.enhancedImageUrl
              }
            : img
        ));

        toast.success(`Enhanced: ${image.name}`);
        
      } catch (error: any) {
        console.error('Enhancement error:', error);
        setEnhancedImages(prev => prev.map(img => 
          img.id === image.id ? { ...img, status: 'failed', progress: 0 } : img
        ));
        toast.error(`Failed: ${image.name}`);
      }

      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsProcessing(false);
    toast.success('All images processed!');
  };

  const downloadImage = (img: EnhancedImage) => {
    if (!img.enhancedUrl) return;
    const link = document.createElement('a');
    link.href = img.enhancedUrl;
    link.download = `enhanced-${img.name}`;
    link.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Image Enhancement Settings
        </CardTitle>
        <CardDescription>
          Enhance image quality with AI-powered upscaling
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enhancement Options */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="mb-3 block">Enhancement Type</Label>
            <RadioGroup value={enhancementType} onValueChange={(v) => setEnhancementType(v as any)}>
              <div className="flex items-center space-x-2 mb-2">
                <RadioGroupItem value="dpi" id="enh-dpi" />
                <Label htmlFor="enh-dpi" className="cursor-pointer">DPI Enhancement</Label>
              </div>
              <div className="flex items-center space-x-2 mb-2">
                <RadioGroupItem value="quality" id="enh-quality" />
                <Label htmlFor="enh-quality" className="cursor-pointer">Quality Boost</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="both" id="enh-both" />
                <Label htmlFor="enh-both" className="cursor-pointer">Both (Recommended)</Label>
              </div>
            </RadioGroup>
          </div>

          {(enhancementType === 'dpi' || enhancementType === 'both') && (
            <div>
              <Label className="mb-3 block">Target DPI: {dpiTarget}</Label>
              <Slider
                value={[dpiTarget]}
                onValueChange={(v) => setDpiTarget(v[0])}
                min={150}
                max={600}
                step={150}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>150</span>
                <span>300</span>
                <span>600</span>
              </div>
            </div>
          )}
          
          {(enhancementType === 'quality' || enhancementType === 'both') && (
            <div>
              <Label className="mb-3 block">Quality Level</Label>
              <RadioGroup value={qualityLevel} onValueChange={(v) => setQualityLevel(v as any)}>
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="standard" id="q-standard" />
                  <Label htmlFor="q-standard" className="cursor-pointer">Standard</Label>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="hd" id="q-hd" />
                  <Label htmlFor="q-hd" className="cursor-pointer">HD (Recommended)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="ultra" id="q-ultra" />
                  <Label htmlFor="q-ultra" className="cursor-pointer">Ultra</Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        {/* Watermark Section */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-base font-semibold">Watermark Settings</Label>
              <p className="text-sm text-muted-foreground mt-1">Add custom watermark to enhanced images</p>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="wm-enable" className="cursor-pointer">Enable</Label>
              <input
                id="wm-enable"
                type="checkbox"
                checked={enableWatermark}
                onChange={(e) => setEnableWatermark(e.target.checked)}
                className="w-5 h-5 cursor-pointer accent-purple-500"
              />
            </div>
          </div>

          {enableWatermark && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="mb-2 block">Watermark Text</Label>
                <input
                  type="text"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  placeholder="Enter watermark text"
                  className="w-full px-4 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              <div>
                <Label className="mb-2 block">Opacity: {watermarkOpacity}%</Label>
                <Slider
                  value={[watermarkOpacity]}
                  onValueChange={(v) => setWatermarkOpacity(v[0])}
                  min={10}
                  max={100}
                  step={10}
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button
          onClick={enhanceImages}
          disabled={isProcessing || enhancedImages.length === 0}
          className="w-full"
          size="lg"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {isProcessing ? 'Processing...' : `Enhance ${enhancedImages.length} Image(s)`}
        </Button>

        {/* Results */}
        {enhancedImages.some(img => img.status !== 'pending') && (
          <div className="space-y-3 mt-6">
            <h3 className="font-semibold text-lg">Processing Results</h3>
            {enhancedImages.map((img) => (
              <div key={img.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{img.name}</span>
                  {img.status === 'completed' && (
                    <Button size="sm" onClick={() => downloadImage(img)}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  )}
                </div>
                {img.status === 'processing' && (
                  <Progress value={img.progress} className="h-2" />
                )}
                {img.status === 'completed' && img.enhancedUrl && (
                  <img src={img.enhancedUrl} alt={img.name} className="w-full rounded mt-2" />
                )}
                {img.status === 'failed' && (
                  <p className="text-sm text-destructive">Enhancement failed</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
